import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Bell, 
  CheckCheck, 
  RefreshCw, 
  Clock,
  User,
  Store,
  Mail,
  Loader,
  ChevronRight,
  CircleDot,
  CreditCard,
  FileText,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  Circle,
  Eye,
  Megaphone,
  TrendingUp,
  BarChart3,
  Link2,
  Users,
  Gift,
  Award
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL;
const LAST_VIEWED_KEY = 'celey:admin:signups:lastViewed';

const AdminSignupsTab = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [lastViewedTimestamp, setLastViewedTimestamp] = useState(null);
  
  // Referral analytics state
  const [referralAnalytics, setReferralAnalytics] = useState(null);
  const [referralPeriod, setReferralPeriod] = useState('30');
  const [loadingReferrals, setLoadingReferrals] = useState(true);
  
  // Phase 2: User referrals state
  const [userReferrals, setUserReferrals] = useState(null);
  const [loadingUserReferrals, setLoadingUserReferrals] = useState(false);
  const [showReferralsTable, setShowReferralsTable] = useState(false);
  const [grantingRewardFor, setGrantingRewardFor] = useState(null); // Track which user is being granted a reward

  // Load last viewed timestamp from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(LAST_VIEWED_KEY);
    if (stored) {
      setLastViewedTimestamp(new Date(stored));
    }
  }, []);

  // Update last viewed timestamp when component mounts/panel is viewed
  useEffect(() => {
    const now = new Date().toISOString();
    localStorage.setItem(LAST_VIEWED_KEY, now);
    // Update state after a small delay to show "new" indicators briefly
    const timer = setTimeout(() => {
      setLastViewedTimestamp(new Date(now));
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/admin/signup-notifications?limit=100&unread_only=${showUnreadOnly}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching signup notifications:', error);
      toast.error('Failed to load signup notifications');
    } finally {
      setLoading(false);
    }
  }, [showUnreadOnly]);

  // Fetch referral analytics
  const fetchReferralAnalytics = useCallback(async () => {
    setLoadingReferrals(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/admin/referral-analytics?days=${referralPeriod}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReferralAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching referral analytics:', error);
    } finally {
      setLoadingReferrals(false);
    }
  }, [referralPeriod]);

  // Phase 2: Fetch user referrals data
  const fetchUserReferrals = useCallback(async () => {
    setLoadingUserReferrals(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/admin/user-referrals?limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserReferrals(response.data);
    } catch (error) {
      console.error('Error fetching user referrals:', error);
    } finally {
      setLoadingUserReferrals(false);
    }
  }, []);

  // Grant referral reward handler
  const handleGrantReward = async (userId, userName) => {
    setGrantingRewardFor(userId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/admin/grant-referral-reward/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Granted 1 month fee waiver to ${userName}`, {
        description: `Rewarded referral: ${response.data.rewarded_referral_email}`
      });
      // Refresh the referrals table to reflect updated data
      fetchUserReferrals();
    } catch (error) {
      console.error('Error granting referral reward:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to grant reward';
      toast.error(errorMsg);
    } finally {
      setGrantingRewardFor(null);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    fetchReferralAnalytics();
  }, [fetchReferralAnalytics]);

  // Fetch user referrals when table is shown
  useEffect(() => {
    if (showReferralsTable && !userReferrals) {
      fetchUserReferrals();
    }
  }, [showReferralsTable, userReferrals, fetchUserReferrals]);

  const markAsRead = async (e, notificationId) => {
    e.stopPropagation(); // Prevent card click
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/api/admin/signup-notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/api/admin/signup-notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleCardClick = (notification) => {
    // Navigate to admin users tab with the user selected/highlighted
    navigate('/admin/dashboard?tab=users&search=' + encodeURIComponent(notification.user_email));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Check if notification is "new" (signed up after last viewed)
  const isNewSinceLastViewed = (signupTimestamp) => {
    if (!lastViewedTimestamp) return true;
    const signupDate = new Date(signupTimestamp);
    return signupDate > lastViewedTimestamp;
  };

  const getRoleBadge = (role, roles) => {
    if (roles?.includes('vendor')) {
      return (
        <Badge className="bg-purple-100 text-purple-800">
          <Store className="w-3 h-3 mr-1" />
          Provider
        </Badge>
      );
    }
    return (
      <Badge className="bg-blue-100 text-blue-800">
        <User className="w-3 h-3 mr-1" />
        Customer
      </Badge>
    );
  };

  // Vendor status indicators
  const getProfileStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Profile Complete
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-amber-100 text-amber-700 text-xs">
            <CircleDot className="w-3 h-3 mr-1" />
            Profile In Progress
          </Badge>
        );
      case 'not_started':
      default:
        return (
          <Badge className="bg-gray-100 text-gray-600 text-xs">
            <Circle className="w-3 h-3 mr-1" />
            Profile Not Started
          </Badge>
        );
    }
  };

  const getStripeStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
            <CreditCard className="w-3 h-3 mr-1" />
            Stripe Connected
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-amber-100 text-amber-700 text-xs">
            <CreditCard className="w-3 h-3 mr-1" />
            Stripe In Progress
          </Badge>
        );
      case 'not_started':
      default:
        return (
          <Badge className="bg-gray-100 text-gray-600 text-xs">
            <CreditCard className="w-3 h-3 mr-1" />
            Stripe Not Started
          </Badge>
        );
    }
  };

  // Customer activity indicator
  const getActivityStatusBadge = (status) => {
    switch (status) {
      case 'inquiry_sent':
        return (
          <Badge className="bg-blue-100 text-blue-700 text-xs">
            <MessageSquare className="w-3 h-3 mr-1" />
            Request Sent
          </Badge>
        );
      case 'no_activity':
      default:
        return (
          <Badge className="bg-gray-100 text-gray-500 text-xs">
            <AlertCircle className="w-3 h-3 mr-1" />
            No Activity Yet
          </Badge>
        );
    }
  };

  // Count new signups since last view
  const newSignupsCount = notifications.filter(n => 
    isNewSinceLastViewed(n.signup_timestamp) && !n.read
  ).length;

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-emerald-600" />
                Recent Signups
              </CardTitle>
              <CardDescription>
                New user registrations with status indicators
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              {newSignupsCount > 0 && (
                <Badge className="bg-emerald-100 text-emerald-800">
                  <Eye className="w-3 h-3 mr-1" />
                  {newSignupsCount} new since last visit
                </Badge>
              )}
              {unreadCount > 0 && (
                <Badge className="bg-red-100 text-red-800">
                  <Bell className="w-3 h-3 mr-1" />
                  {unreadCount} unread
                </Badge>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchNotifications}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button
                variant={showUnreadOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowUnreadOnly(!showUnreadOnly)}
              >
                {showUnreadOnly ? 'Showing Unread' : 'Show All'}
              </Button>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Mark all read
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Referral Analytics Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                Referral Sources
              </CardTitle>
              <CardDescription>
                How users are discovering Celey
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={referralPeriod} onValueChange={setReferralPeriod}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchReferralAnalytics}
                disabled={loadingReferrals}
              >
                <RefreshCw className={`w-4 h-4 ${loadingReferrals ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingReferrals ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : referralAnalytics ? (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-900">{referralAnalytics.total_signups}</div>
                  <div className="text-xs text-gray-500">Total Signups</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-600">{referralAnalytics.signups_with_source}</div>
                  <div className="text-xs text-gray-500">With Source</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{referralAnalytics.referred_signups || 0}</div>
                  <div className="text-xs text-gray-500">Via Referral</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-400">{referralAnalytics.signups_without_source}</div>
                  <div className="text-xs text-gray-500">No Source</div>
                </div>
              </div>
              
              {/* Source Breakdown */}
              {referralAnalytics.source_breakdown?.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    Top Sources
                  </h4>
                  <div className="space-y-2">
                    {referralAnalytics.source_breakdown.slice(0, 10).map((item, index) => (
                      <div key={item.normalized} className="flex items-center gap-3">
                        <div className="w-6 text-center text-sm text-gray-400">#{index + 1}</div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-800">{item.source}</span>
                            <span className="text-sm text-gray-600">{item.count} ({item.percentage}%)</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all"
                              style={{ width: `${Math.min(item.percentage * 2, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <Megaphone className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No referral data yet</p>
                  <p className="text-xs text-gray-400">Users will be asked how they found Celey during signup</p>
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Phase 2: User Referrals Table (Read-only) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-emerald-600" />
                User Referrals
              </CardTitle>
              <CardDescription className="flex items-center gap-2 flex-wrap">
                <span>Track referral codes and who referred whom</span>
                {userReferrals?.global_eligible_referrals > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                    {userReferrals.global_eligible_referrals} eligible referral{userReferrals.global_eligible_referrals !== 1 ? 's' : ''} — reward available
                  </Badge>
                )}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant={showReferralsTable ? "default" : "outline"} 
                size="sm"
                onClick={() => setShowReferralsTable(!showReferralsTable)}
              >
                {showReferralsTable ? 'Hide Table' : 'View Table'}
              </Button>
              {showReferralsTable && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchUserReferrals}
                  disabled={loadingUserReferrals}
                >
                  <RefreshCw className={`w-4 h-4 ${loadingUserReferrals ? 'animate-spin' : ''}`} />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        {showReferralsTable && (
          <CardContent>
            {loadingUserReferrals ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-6 h-6 animate-spin text-emerald-600" />
              </div>
            ) : userReferrals?.users?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-gray-600">User</th>
                      <th className="text-left py-2 px-2 font-medium text-gray-600">Role</th>
                      <th className="text-left py-2 px-2 font-medium text-gray-600">Referral Code</th>
                      <th className="text-center py-2 px-2 font-medium text-gray-600" title="Pending / Eligible / Rewarded">
                        Referral Status
                      </th>
                      <th className="text-left py-2 px-2 font-medium text-gray-600">Reward Status</th>
                      <th className="text-left py-2 px-2 font-medium text-gray-600">Referred By</th>
                      <th className="text-center py-2 px-2 font-medium text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userReferrals.users.map(user => {
                      const hasActiveReward = user.platform_fee_disabled_until && 
                        new Date(user.platform_fee_disabled_until) > new Date();
                      const rewardStatus = hasActiveReward 
                        ? `Active until ${new Date(user.platform_fee_disabled_until).toLocaleDateString()}`
                        : user.referral_reward_months_granted > 0 
                          ? `${user.referral_reward_months_granted} month(s) granted (expired)`
                          : 'None';
                      
                      // Calculate totals from new API fields
                      const pendingCount = user.pending_referrals || 0;
                      const eligibleCount = user.eligible_referrals || 0;
                      const rewardedCount = user.rewarded_referrals || 0;
                      const totalReferrals = pendingCount + eligibleCount + rewardedCount;
                      
                      return (
                        <tr key={user.id} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-2">
                            <div className="font-medium text-gray-900">{user.name}</div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                            {/* Show this user's own referral status (if they were referred) */}
                            {user.own_referral_status && (
                              <div className="mt-1">
                                {user.own_referral_status.status === 'pending' && (
                                  <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                                    <Clock className="w-2.5 h-2.5 mr-0.5" />
                                    Pending
                                    {user.own_referral_status.is_vendor && user.own_referral_status.profile_status && (
                                      <span className="ml-1 opacity-75">
                                        ({user.own_referral_status.profile_status})
                                      </span>
                                    )}
                                  </Badge>
                                )}
                                {user.own_referral_status.status === 'eligible' && (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                    <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                                    Eligible
                                  </Badge>
                                )}
                                {user.own_referral_status.status === 'rewarded' && (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">
                                    <Award className="w-2.5 h-2.5 mr-0.5" />
                                    Rewarded
                                  </Badge>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-2">
                            <Badge variant={user.role === 'vendor' ? 'default' : 'secondary'} className="text-xs">
                              {user.role === 'vendor' ? 'Provider' : 'Customer'}
                            </Badge>
                          </td>
                          <td className="py-2 px-2">
                            <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                              {user.referral_code || 'N/A'}
                            </code>
                          </td>
                          <td className="py-2 px-2 text-center">
                            {totalReferrals > 0 ? (
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-xs text-gray-500">{totalReferrals} total</span>
                                <div className="flex items-center gap-1 flex-wrap justify-center">
                                  {pendingCount > 0 && (
                                    <Badge className="bg-yellow-100 text-yellow-700 text-xs" title="Pending: Waiting for referral to complete provider profile">
                                      <Clock className="w-2.5 h-2.5 mr-0.5" />
                                      {pendingCount}
                                    </Badge>
                                  )}
                                  {eligibleCount > 0 && (
                                    <Badge className="bg-emerald-100 text-emerald-700 text-xs" title="Eligible: Provider with complete profile">
                                      <Gift className="w-2.5 h-2.5 mr-0.5" />
                                      {eligibleCount}
                                    </Badge>
                                  )}
                                  {rewardedCount > 0 && (
                                    <Badge className="bg-blue-100 text-blue-700 text-xs" title="Rewarded: Already granted">
                                      <Award className="w-2.5 h-2.5 mr-0.5" />
                                      {rewardedCount}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                          <td className="py-2 px-2">
                            {hasActiveReward ? (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                <Award className="w-3 h-3 mr-1" />
                                {rewardStatus}
                              </Badge>
                            ) : user.referral_reward_months_granted > 0 ? (
                              <span className="text-xs text-gray-500">{rewardStatus}</span>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="py-2 px-2">
                            {user.referrer ? (
                              <div className="text-xs">
                                <div className="text-gray-700">{user.referrer.name}</div>
                                <div className="text-gray-400">({user.referrer.referral_code})</div>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-center">
                            <Button
                              size="sm"
                              variant={eligibleCount > 0 ? "default" : "outline"}
                              disabled={eligibleCount === 0 || grantingRewardFor === user.id}
                              onClick={() => handleGrantReward(user.id, user.name)}
                              className="text-xs"
                              data-testid={`grant-reward-btn-${user.id}`}
                              title={eligibleCount === 0 ? (pendingCount > 0 ? `${pendingCount} pending (not yet eligible)` : 'No referrals') : `Grant reward for ${eligibleCount} eligible referral(s)`}
                            >
                              {grantingRewardFor === user.id ? (
                                <Loader className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <Gift className="w-3 h-3 mr-1" />
                                  Grant 1 Mo
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {userReferrals.total > userReferrals.users.length && (
                  <div className="text-center py-3 text-sm text-gray-500">
                    Showing {userReferrals.users.length} of {userReferrals.total} users
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Link2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No referral codes generated yet</p>
                <p className="text-xs text-gray-400">Users will get unique referral codes when they sign up</p>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Notifications List */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <UserPlus className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No signups yet</p>
              <p className="text-sm">New user registrations will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification) => {
                const isVendor = notification.user_roles?.includes('vendor');
                const isNewSignup = isNewSinceLastViewed(notification.signup_timestamp);
                
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleCardClick(notification)}
                    className={`p-4 rounded-lg border transition-all cursor-pointer hover:shadow-md hover:border-emerald-300 ${
                      notification.read 
                        ? 'bg-white border-gray-200' 
                        : 'bg-emerald-50 border-emerald-200'
                    } ${isNewSignup && !notification.read ? 'ring-2 ring-emerald-300 ring-offset-1' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {/* Avatar */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isVendor ? 'bg-purple-100' : 'bg-blue-100'
                        }`}>
                          {isVendor 
                            ? <Store className="w-5 h-5 text-purple-600" />
                            : <User className="w-5 h-5 text-blue-600" />
                          }
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {/* Name and Role */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">
                              {notification.user_name}
                            </span>
                            {getRoleBadge(notification.user_role, notification.user_roles)}
                            {!notification.read && (
                              <span className="w-2 h-2 bg-emerald-500 rounded-full" title="Unread"></span>
                            )}
                            {isNewSignup && !notification.read && (
                              <Badge className="bg-emerald-500 text-white text-xs px-1.5 py-0">
                                NEW
                              </Badge>
                            )}
                          </div>
                          
                          {/* Email */}
                          <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                            <Mail className="w-3 h-3" />
                            <span className="truncate">{notification.user_email}</span>
                          </div>
                          
                          {/* Discovery Source - supports both legacy and new fields */}
                          {(notification.discovery_source || notification.referral_source) && (
                            <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                              <Megaphone className="w-3 h-3" />
                              <span>via {
                                notification.discovery_source === 'Other' && notification.discovery_source_other
                                  ? `Other: ${notification.discovery_source_other}`
                                  : notification.discovery_source || notification.referral_source
                              }</span>
                            </div>
                          )}
                          
                          {/* Referral indicator - if user was referred */}
                          {notification.referred_by_user_id && (
                            <div className="flex items-center gap-1 text-xs text-emerald-600 mt-1">
                              <Link2 className="w-3 h-3" />
                              <span>Referred via code: {notification.referrer_code}</span>
                            </div>
                          )}
                          
                          {/* Timestamp */}
                          <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                            <Clock className="w-3 h-3" />
                            <span>{formatDate(notification.signup_timestamp)}</span>
                          </div>
                          
                          {/* Status Indicators */}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {isVendor ? (
                              <>
                                {getProfileStatusBadge(notification.profile_completion)}
                                {getStripeStatusBadge(notification.stripe_status)}
                              </>
                            ) : (
                              getActivityStatusBadge(notification.activity_status)
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!notification.read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => markAsRead(e, notification.id)}
                            className="text-xs"
                          >
                            Mark read
                          </Button>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSignupsTab;
